import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { insideCategories, insidePages } from '../data/content';
import { OFFER_ANCHOR } from '../data/config';
import { CtaButton } from './CtaButton';
const AUTOPLAY_MS = 4000;
export function InsideLookSection() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const dragged = useRef(false);
  const total = insidePages.length;
  const go = (dir: 1 | -1) => setIndex(i => (i + dir + total) % total);
  const page = insidePages[index];
  useEffect(() => {
    if (paused) return;
    const t = setTimeout(() => setIndex(i => (i + 1) % total), AUTOPLAY_MS);
    return () => clearTimeout(t);
  }, [index, paused, total]);
  return <section className="bg-slate-100 px-4 py-6 text-center md:px-12 md:py-8" data-id="element-110" data-mp="17qo2y6bd8acm">
      <h2 className="text-[24px] font-extrabold uppercase leading-[1.15] tracking-[-0.3px] text-blue-950 md:text-[32px]" data-id="element-111" data-mp="12smj3j648703">
        Take a look inside
      </h2>
      <p className="mt-1.5 text-[14px] font-bold uppercase leading-tight text-teal-700 md:text-[16px]" data-id="element-112" data-mp="2s9i4egr6lvzn">
        Explore the Complete Postoperative Knee Rehabilitation System™
      </p>
      <p className="mx-auto mt-1.5 max-w-[600px] text-[13px] leading-[1.45] text-slate-600 md:text-[14px]" data-id="element-113" data-mp="2x71qu3y19f2o">
        Each volume focuses on a different group of surgical procedures and provides resources for its specific
        rehabilitation requirements.
      </p>

      <div className="relative mx-auto mt-4 max-w-[440px]" role="region" aria-roledescription="carousel" aria-label="Page previews" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onFocus={() => setPaused(true)} onBlur={() => setPaused(false)} data-id="element-114" data-mp="29jtsk5z0v6a5">
        <div className="overflow-hidden rounded-md border border-slate-200 bg-slate-950 shadow-md" data-id="element-115" data-mp="1qsj0vmlh2ph7">
          <AnimatePresence mode="wait" initial={false} data-id="element-116" data-mp="1iruyhm10tnm5">
            <motion.a key={index} href={OFFER_ANCHOR} drag="x" dragConstraints={{
            left: 0,
            right: 0
          }} dragElastic={0.2} onDragStart={() => dragged.current = true} onDragEnd={(_, info) => {
            if (info.offset.x < -50) go(1);else if (info.offset.x > 50) go(-1);
            setTimeout(() => dragged.current = false, 0);
          }} onClick={e => dragged.current && e.preventDefault()} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.2,
            ease: 'easeOut'
          }} className="block aspect-[4/5] w-full cursor-grab touch-pan-y active:cursor-grabbing" aria-label={`Preview ${index + 1} of ${total} — go to the special offer`} data-id="element-117" data-mp="1iqa2rt2yzymp">
              <img src={page.image} alt={page.alt} className="h-full w-full object-contain" draggable={false} data-id="element-118" data-mp="2g9uxgwowuzdo" />
            </motion.a>
          </AnimatePresence>
        </div>
        <button type="button" onClick={() => go(-1)} aria-label="Previous preview" className="absolute left-2 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-slate-200 bg-white text-blue-950 shadow-md transition-colors duration-150 hover:text-teal-700" data-id="element-119" data-mp="70dn0gii0j8v">
          <ChevronLeftIcon className="h-5 w-5" data-id="element-120" data-mp="pp24eshrw6fw" />
        </button>
        <button type="button" onClick={() => go(1)} aria-label="Next preview" className="absolute right-2 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-slate-200 bg-white text-blue-950 shadow-md transition-colors duration-150 hover:text-teal-700" data-id="element-121" data-mp="prel72bsoard">
          <ChevronRightIcon className="h-5 w-5" data-id="element-122" data-mp="19djc726tpvua" />
        </button>
      </div>

      <div className="mt-3 flex justify-center gap-1.5" role="tablist" aria-label="Choose preview" data-id="element-123" data-mp="3gwulgjetap5y">
        {insidePages.map((p, i) => <button key={p.image} type="button" role="tab" onClick={() => setIndex(i)} aria-label={`Show preview ${i + 1}`} aria-selected={i === index} className={`h-2 rounded-full transition-[width,background-color] duration-200 ease-out ${i === index ? 'w-5 bg-blue-950' : 'w-2 bg-slate-300'}`} data-id="element-124" data-mp="3bwzy2pu8whpn" data-mpi={i} />)}
      </div>

      <dl className="mt-5 grid gap-x-6 gap-y-3 text-left sm:grid-cols-2" data-id="element-125" data-mp="3vo4o6o7mzcdl">
        {insideCategories.map(c => <div key={c.title} className="border-l-[3px] border-teal-700 pl-3" data-id="element-126" data-mp="3lvz0hqof3h47">
            <dt className="text-[13px] font-extrabold uppercase leading-tight text-blue-950 md:text-[14px]" data-id="element-127" data-mp="3ltusoxnedxyn">
              {c.title}
            </dt>
            <dd className="mt-0.5 text-[13px] leading-[1.4] text-slate-600 md:text-[14px]" data-id="element-128" data-mp="ie7auixr7996">{c.text}</dd>
          </div>)}
      </dl>

      <p className="mt-5 text-[13px] font-extrabold uppercase leading-tight text-amber-700 md:text-[15px]" data-id="element-129" data-mp="pshmjc3gjbrv">
        Practical resources. Organized by clinical purpose.
      </p>
      <div className="mt-2.5" data-id="element-130" data-mp="zmzrsloivfds">
        <CtaButton mode="scroll" subtext="Explore the special launch offer" data-id="element-131" data-mp="zqxetie80r2y">
          Yes! I want all 12 professional resources!
        </CtaButton>
      </div>
    </section>;
}